import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { queryClient } from '@/main';
import { fromApiOrganizationUserFieldAdapter } from '@/resources/organization-user/organization-user.field-adapter';
import { fromApiUserFieldAdapter } from '@/resources/user/user.field-adapter';
import type { QueryFnResult, QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

export const organizationUserQueries = queryNamespace(Resource.organizationUser, {
  list: (params?: operations['OrganizationUserController_getMany']['parameters']['query']) => queryOptions({
    queryKey: [ params ],
    queryFn: ({ signal }) =>
      client.GET('/customer/api/tcd/users', {
        signal,
        params: {
          query: {
            page: params?.page ?? 1,
            limit: params?.limit ?? 100,
            sortBy: params?.sortBy,
            email: params?.email,
            id: params?.id,
            role: params?.role,
          },
        },
      }).then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({
            message: Array.isArray(error.message) ? error.message[0] : error.message,
            response,
            isGlobal: false,
          });
        }

        return data;
      }),
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.items.map((record) => ({
        original: record,
        transformer: fromApiOrganizationUserFieldAdapter.transform(record),
        meta: {
          fullName: fromApiUserFieldAdapter.getFullName(record),
        },
      })),
    }),
  }),
});

export const organizationUserMutations = mutationNamespace(Resource.organizationUser, {
  update: mutationOptions({
    mutationFn: ({ id, body }: { id: string; body: components['schemas']['UpdateOrganizationUserDto'] }) =>
      client
        .PATCH('/customer/api/tcd/users/{id}', {
          params: { path: { id }},
          body,
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }

          return { data, status: response.status };
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: organizationUserQueries.all.queryKey });
    },
  }),
});

export type OrganizationUserListQueryFnResult = QueryFnResult<typeof organizationUserQueries.list>;
export type OrganizationUserListQueryResultData = QueryResultData<typeof organizationUserQueries.list>;
